// src/components/store/sections/navbar/NavbarMinimalLuxe.tsx
"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { 
  Search, 
  Store, 
  Sparkles, 
  ChevronDown, 
  Check, 
  Loader2, 
  ArrowLeft, 
  X, 
  Menu, 
  Layers, 
  Flame, 
  Package, 
  ArrowRight,
  Camera,
} from "lucide-react";
import UserHeaderMenu from "@/components/store/UserHeaderMenu";
import CartHeaderButton from "@/components/store/CartHeaderButton";
import ShopCardsModal, { ShopItem } from "@/components/store/sections/shop/ShopCardsModal";
import VisualSearchModal from "./VisualSearchModal";
import { useNavbarBranding, NavbarBrandIdentity, NavbarBrandingConfig } from "./brandingConfig";

interface NavbarProps {
  subdomain?: string;
  storeName?: string;
  logo?: string | null;
  primaryColor?: string;
  secondaryColor?: string;
  storeTagline?: string;
  brandingConfig?: Partial<NavbarBrandingConfig>;
  isPreview?: boolean;
}

export default function NavbarMinimalLuxe({
  subdomain = "nextlook",
  storeName = "Nextlook",
  logo = "/logo.png",
  primaryColor = "#059669",
  secondaryColor = "#047857",
  storeTagline,
  brandingConfig,
}: NavbarProps) {
  const {
    branding,
    effectiveStoreName,
    effectiveTagline,
    effectiveLogo,
    effectivePrimaryColor,
    effectiveSecondaryColor,
    mainBarStyle,
    subBarStyle,
  } = useNavbarBranding({
    subdomain,
    storeName,
    storeTagline,
    logo,
    primaryColor,
    secondaryColor,
    brandingConfig,
  });

  storeName = effectiveStoreName || storeName;
  logo = effectiveLogo;
  primaryColor = effectivePrimaryColor;
  secondaryColor = effectiveSecondaryColor;
  const router = useRouter();

  // Shop / Outlet States
  const [shopsList, setShopsList] = useState<ShopItem[]>([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [selectedShopId, setSelectedShopId] = useState<string>("all");
  const [isShopMenuOpen, setIsShopMenuOpen] = useState(false);
  const [isAllShopsModalOpen, setIsAllShopsModalOpen] = useState(false);
  const shopDropdownRef = useRef<HTMLDivElement>(null);

  // Search States
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isVisualSearchOpen, setIsVisualSearchOpen] = useState(false);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  // Mobile Drawer State
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const shopParam = params.get("shop");
      if (shopParam) {
        setSelectedShopId(shopParam);
      } else {
        setSelectedShopId("all");
      }
    }
  }, []);

  useEffect(() => {
    if (isMobileSearchOpen && mobileSearchInputRef.current) {
      mobileSearchInputRef.current.focus();
    }
  }, [isMobileSearchOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (shopDropdownRef.current && !shopDropdownRef.current.contains(e.target as Node)) {
        setIsShopMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Shops Fetch
  useEffect(() => {
    async function fetchShops() {
      setShopsLoading(true);
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");

        const apiBase = isLocal ? `http://${subdomain}.localhost:8000` : "";
        const apiUrl = `${apiBase}/api/v1/stores/shops/`;

        const token =
          typeof window !== "undefined"
            ? localStorage.getItem("merchant_token") ||
              localStorage.getItem("token") ||
              localStorage.getItem("customer_token") ||
              ""
            : "";

        const res = await fetch(apiUrl, {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data)
            ? data
            : data?.results || data?.data || data?.shops || [];
          setShopsList(list);
        }
      } catch (err) {
        console.error("Error fetching shops in NavbarMinimalLuxe:", err);
      } finally {
        setShopsLoading(false);
      }
    }
    fetchShops();
  }, [subdomain]);

  const handleSelectShop = (shopId: string | number) => {
    setIsShopMenuOpen(false);
    setIsAllShopsModalOpen(false);
    setSelectedShopId(String(shopId));
    if (shopId === "all") {
      router.push("/products");
    } else {
      router.push(`/products?shop=${shopId}`);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsMobileSearchOpen(false);
    if (searchQuery.trim()) {
      const shopParam = selectedShopId !== "all" ? `&shop=${selectedShopId}` : "";
      router.push(`/products?search=${encodeURIComponent(searchQuery.trim())}${shopParam}`);
    }
  };

  const currentSelectedShop = shopsList.find((s) => String(s.id) === selectedShopId);
  const currentShopLabel = currentSelectedShop ? currentSelectedShop.name : "All Outlets";

  return (
    <header style={mainBarStyle} className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-md border-b border-slate-100 select-none font-sans">
      
      {/* ১. MOBILE FULL-WIDTH SEARCH OVERLAY */}
      {isMobileSearchOpen && (
        <div className="absolute inset-0 z-50 bg-white px-3 sm:px-4 flex items-center gap-2 border-b border-slate-200 animate-in fade-in duration-150">
          <button
            type="button"
            onClick={() => setIsMobileSearchOpen(false)}
            className="p-1.5 rounded-full hover:bg-slate-100 text-slate-600 transition-colors cursor-pointer shrink-0"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <form onSubmit={handleSearchSubmit} className="flex-1 flex items-center h-10 border border-slate-200 rounded-full px-3.5 bg-slate-50 shadow-xs">
            <input
              ref={mobileSearchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search luxury collections, products..."
              className="flex-1 h-full text-xs text-slate-800 bg-transparent outline-none placeholder:text-slate-400"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setIsMobileSearchOpen(false);
                setIsVisualSearchOpen(true);
              }}
              title="Search by Photo or Camera"
              className="p-1.5 text-slate-500 hover:text-slate-800 cursor-pointer shrink-0"
            >
              <Camera className="w-4 h-4" style={{ color: primaryColor }} />
            </button>
            <button
              type="submit"
              style={{ color: primaryColor }}
              className="p-1.5 font-bold cursor-pointer shrink-0"
            >
              <Search className="w-4 h-4 stroke-[2.5]" />
            </button>
          </form>
        </div>
      )}

      {/* ২. মূল ন্যাভবার কন্টেইনার */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-2 sm:gap-4 lg:gap-6">
        
        {/* Mobile Hamburger + Brand Logo */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <button
            type="button"
            onClick={() => setMobileDrawerOpen(true)}
            className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          <NavbarBrandIdentity
            href="/"
            branding={branding}
            storeName={storeName}
            tagline={effectiveTagline}
            logo={logo}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            isDarkTheme={false}
            variantStyle="minimal_luxe"
          />
        </div>

        {/* ৩. Select Outlet Dropdown */}
        <div ref={shopDropdownRef} className="relative hidden xl:flex items-center shrink-0">
          <button
            type="button"
            onClick={() => setIsShopMenuOpen(!isShopMenuOpen)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-300 bg-slate-50/70 text-slate-800 transition-all cursor-pointer group text-left shadow-xs"
            title="Select Outlet"
          >
            <div 
              style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
              className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
            >
              <Store className="w-3.5 h-3.5" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[9px] text-slate-400 font-semibold uppercase tracking-wider">Outlet</span>
              <span className="text-xs font-bold truncate max-w-[95px]">{currentShopLabel}</span>
            </div>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isShopMenuOpen ? "rotate-180" : ""}`} />
          </button>

          {isShopMenuOpen && (
            <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-slate-200 z-[999] py-1.5 text-slate-800 animate-in fade-in zoom-in-95 duration-150">
              <div className="px-3.5 py-1.5 border-b border-slate-100 text-[10px] font-black uppercase tracking-wider text-slate-400">
                Select Shop / Outlet
              </div>
              <button
                type="button"
                onClick={() => handleSelectShop("all")}
                className={`w-full text-left px-3.5 py-2 text-xs font-bold hover:bg-slate-50 flex items-center justify-between cursor-pointer ${
                  selectedShopId === "all" ? "bg-slate-50 font-black" : ""
                }`}
                style={selectedShopId === "all" ? { color: primaryColor } : undefined}
              >
                <span>🏪 Main Store (All)</span>
                {selectedShopId === "all" && <Check className="w-3.5 h-3.5" />}
              </button>

              {shopsLoading ? (
                <div className="px-3.5 py-2.5 text-xs text-slate-400 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Loading outlets...</span>
                </div>
              ) : shopsList.length === 0 ? (
                <div className="px-3.5 py-2 text-xs text-slate-400 font-medium">
                  No other outlets found
                </div>
              ) : (
                shopsList.map((shop) => (
                  <button
                    key={shop.id}
                    type="button"
                    onClick={() => handleSelectShop(shop.id)}
                    className={`w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center justify-between cursor-pointer truncate ${
                      String(shop.id) === selectedShopId ? "bg-slate-50 font-bold" : "text-slate-700"
                    }`}
                    style={String(shop.id) === selectedShopId ? { color: primaryColor } : undefined}
                  >
                    <div className="truncate">
                      <span className="truncate block font-bold">{shop.name}</span>
                      {shop.category_label && (
                        <span className="text-[9px] text-slate-400 font-mono block">
                          {shop.category_label}
                        </span>
                      )}
                    </div>
                    {String(shop.id) === selectedShopId && <Check className="w-3.5 h-3.5 shrink-0" />}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* ৪. "Shops" Story Cards Trigger Button */}
        <div className="hidden lg:flex items-center shrink-0">
          <button
            type="button"
            onClick={() => setIsAllShopsModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-purple-200 bg-purple-50/70 hover:bg-purple-100/70 text-purple-900 transition-all cursor-pointer group select-none shadow-xs"
            title="Explore All Shops & Stories"
          >
            <Sparkles className="w-3.5 h-3.5 text-purple-600 animate-pulse shrink-0" />
            <div className="flex flex-col leading-tight text-left">
              <span className="text-[9px] text-purple-500 font-semibold uppercase tracking-wider">Explore</span>
              <span className="text-xs font-black text-purple-950 flex items-center gap-1">
                <span>Shops</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              </span>
            </div>
          </button>
        </div>

        {/* ৫. Desktop Search Bar */}
        <div className="hidden md:flex flex-1 max-w-sm lg:max-w-md items-center">
          <form 
            onSubmit={handleSearchSubmit}
            className="w-full flex items-center border border-slate-200 rounded-full pl-3.5 pr-1 py-1 bg-slate-50/60 focus-within:bg-white focus-within:border-slate-300 transition-all shadow-xs"
          >
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="flex-1 text-xs text-slate-800 bg-transparent outline-none placeholder:text-slate-400"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="p-1 mr-1 text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}

            {/* 🌟 Desktop Visual Search Camera / Lens Button */}
            <button
              type="button"
              onClick={() => setIsVisualSearchOpen(true)}
              title="Search by Photo or Camera"
              className="p-1.5 mr-1 rounded-full hover:bg-slate-200/70 text-slate-500 transition-colors cursor-pointer shrink-0"
            >
              <Camera className="w-3.5 h-3.5" style={{ color: primaryColor }} />
            </button>

            <button
              type="submit"
              style={{ backgroundColor: primaryColor }}
              className="p-1.5 rounded-full text-white hover:opacity-90 active:scale-95 transition-all flex items-center justify-center cursor-pointer shrink-0"
              aria-label="Search"
            >
              <Search className="w-3.5 h-3.5 stroke-[2.5]" />
            </button>
          </form>
        </div>

        {/* ৬. ইউজার অ্যাকশনস & মোবাইল সার্চ ট্রিগার */}
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          {/* Mobile Visual Search Camera Trigger */}
          <button
            type="button"
            onClick={() => setIsVisualSearchOpen(true)}
            className="hidden p-2 rounded-full hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer"
            aria-label="Visual Search"
            title="Search by Photo or Camera"
          >
            <Camera className="w-5 h-5" style={{ color: primaryColor }} />
          </button>

          {/* Mobile Search Icon Trigger Button */}
          <button
            type="button"
            onClick={() => setIsMobileSearchOpen(true)}
            className="md:hidden p-2 rounded-full hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer"
            aria-label="Search"
          >
            <Search className="w-5 h-5" />
          </button>

          <nav className="hidden lg:flex items-center gap-5 text-xs font-bold uppercase tracking-wider text-slate-600 mr-1">
            <Link href="/products" className="hover:text-emerald-600 transition-colors">
              Shop
            </Link>
            <Link href="/categories" className="hover:text-emerald-600 transition-colors">
              Collections
            </Link>
          </nav>

          <UserHeaderMenu />
          <CartHeaderButton />
        </div>
      </div>

      {/* ৭. Sub-Bar for Mobile & Tablet */}
      <div style={subBarStyle} className="lg:hidden bg-slate-50 border-t border-slate-100 px-3 py-1.5 flex items-center gap-2 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <button
          type="button"
          onClick={() => setMobileDrawerOpen(true)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-800 font-bold text-xs shadow-xs shrink-0"
        >
          <Store className="w-3.5 h-3.5" style={{ color: primaryColor }} />
          <span className="truncate max-w-[85px]">{currentShopLabel}</span>
          <ChevronDown className="w-3 h-3 text-slate-400" />
        </button>

        <button
          type="button"
          onClick={() => setIsAllShopsModalOpen(true)}
          className="flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-purple-500/15 to-pink-500/15 border border-purple-300 text-purple-900 font-black text-xs shadow-xs shrink-0"
        >
          <Sparkles className="w-3.5 h-3.5 text-purple-600" />
          <span>All Shops</span>
        </button>

        <Link
          href="/products"
          className="px-2.5 py-1 text-xs font-bold text-slate-600 hover:text-slate-900 shrink-0"
        >
          Shop
        </Link>
        <Link
          href="/categories"
          className="px-2.5 py-1 text-xs font-bold text-slate-600 hover:text-slate-900 shrink-0"
        >
          Collections
        </Link>
      </div>

      {/* ৮. SHOP CARDS MODAL CALL */}
      <ShopCardsModal
        isOpen={isAllShopsModalOpen}
        onClose={() => setIsAllShopsModalOpen(false)}
        shopsList={shopsList}
        shopsLoading={shopsLoading}
        onSelectShop={handleSelectShop}
        primaryColor={primaryColor}
      />

      {/* 🌟 ৮.৫ AI VISUAL SEARCH MODAL */}
      <VisualSearchModal
        isOpen={isVisualSearchOpen}
        onClose={() => setIsVisualSearchOpen(false)}
        subdomain={subdomain}
        primaryColor={primaryColor}
      />

      {/* ৯. RESPONSIVE MOBILE DRAWER */}
      {mobileDrawerOpen && (
        <div className="fixed inset-0 z-[999999] flex animate-in fade-in duration-200">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-xs"
            onClick={() => setMobileDrawerOpen(false)}
          />

          <div className="relative w-72 sm:w-80 max-w-[85vw] bg-white text-slate-900 h-full shadow-2xl flex flex-col justify-between z-10 animate-in slide-in-from-left duration-300">
            <div>
              <div 
                className="text-white p-4 flex items-center justify-between"
                style={{ backgroundColor: primaryColor }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs text-white">
                    {storeName.charAt(0)}
                  </div>
                  <span className="font-bold text-sm tracking-wide">Navigation</span>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileDrawerOpen(false)}
                  className="p-1 rounded-md hover:bg-white/10 text-white cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4 overflow-y-auto max-h-[80vh]">
                {shopsList.length > 0 && (
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">
                      Shops & Outlets
                    </h4>
                    <div className="space-y-1 text-xs font-bold text-slate-700">
                      <button
                        type="button"
                        onClick={() => {
                          handleSelectShop("all");
                          setMobileDrawerOpen(false);
                        }}
                        className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left ${
                          selectedShopId === "all" ? "bg-slate-100 font-black" : "hover:bg-slate-50"
                        }`}
                        style={selectedShopId === "all" ? { color: primaryColor } : undefined}
                      >
                        <span>🏪 Main Store (All)</span>
                        {selectedShopId === "all" && <Check className="w-3.5 h-3.5" />}
                      </button>
                      {shopsList.map((shop) => (
                        <button
                          key={shop.id}
                          type="button"
                          onClick={() => {
                            handleSelectShop(shop.id);
                            setMobileDrawerOpen(false);
                          }}
                          className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left ${
                            String(shop.id) === selectedShopId ? "bg-slate-100 font-black" : "hover:bg-slate-50"
                          }`}
                          style={String(shop.id) === selectedShopId ? { color: primaryColor } : undefined}
                        >
                          <div className="truncate">
                            <span className="block font-bold">{shop.name}</span>
                            {shop.category_label && (
                              <span className="text-[10px] text-slate-400 font-normal">{shop.category_label}</span>
                            )}
                          </div>
                          {String(shop.id) === selectedShopId && <Check className="w-3.5 h-3.5 shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-slate-100 pt-3">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Explore</h4>
                  <div className="space-y-1 text-sm font-semibold text-slate-700">
                    <button
                      type="button"
                      onClick={() => {
                        setMobileDrawerOpen(false);
                        setIsAllShopsModalOpen(true);
                      }}
                      className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100 text-left font-bold text-purple-700"
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <span>All Brand Shops</span>
                      </div>
                      <ArrowRight className="w-4 h-4" />
                    </button>

                    <Link
                      href="/products"
                      onClick={() => setMobileDrawerOpen(false)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-100"
                    >
                      <span>Shop All Products</span>
                      <Package className="w-4 h-4 text-slate-400" />
                    </Link>

                    <Link
                      href="/categories"
                      onClick={() => setMobileDrawerOpen(false)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-100"
                    >
                      <span>Collections</span>
                      <Layers className="w-4 h-4 text-slate-400" />
                    </Link>

                    <Link
                      href="/products?bestseller=true"
                      onClick={() => setMobileDrawerOpen(false)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-100"
                    >
                      <span>Best Sellers</span>
                      <Flame className="w-4 h-4 text-amber-500" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 text-[11px] text-slate-400 font-mono text-center">
              © {storeName} • Minimal Luxe Navigation
            </div>
          </div>
        </div>
      )}
    </header>
  );
}